/**
 * Copies the provided text to the user's clipboard using the Clipboard API.
 *
 * This function ensures that the Clipboard API is available and throws an error
 * if it is not supported or if the operation fails.
 *
 * @param {string} text - The text string to be copied to the clipboard.
 * @throws {Error} Throws an error if the Clipboard API is not available or if
 * the write operation fails.
 * @returns {Promise<void>} Resolves when the text is successfully copied to the
 * clipboard.
 *
 * @example
 * // Example usage:
 * copyToClipboard('Hello, world!')
 *   .then(() => console.log('Text copied successfully!'))
 *   .catch(err => console.error('Failed to copy text:', err));
 */
export const copyToClipboard = async (text: string): Promise<void> => {
  if (!navigator.clipboard) {
    throw new Error('Clipboard API not available. Make sure you are in a secure context (HTTPS).');
  }

  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    throw new Error(`Failed to copy text to clipboard: ${error as Error}`);
  }
};
